import React, { useState } from 'react';

interface LinkedInGuidePDFExportProps {
  contentRef: React.RefObject<HTMLDivElement>;
}

const LinkedInGuidePDFExport: React.FC<LinkedInGuidePDFExportProps> = ({ contentRef }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prepareContentForPDF = (contentElement: HTMLElement): string => {
    // Clone the element to avoid modifying the original DOM
    const contentClone = contentElement.cloneNode(true) as HTMLElement;
    
    try {
      // Extract all existing styles
      const extractOriginalStyles = () => {
        let originalStyles = '';
        try {
          originalStyles = Array.from(document.styleSheets)
            .filter(sheet => {
              try {
                return sheet.cssRules.length > 0;
              } catch (e) {
                return false;
              }
            })
            .map(sheet => {
              return Array.from(sheet.cssRules)
                .map(rule => rule.cssText)
                .join('\n');
            }).join('\n');
        } catch (e) {
          console.warn('Error extracting original styles:', e);
        }
        return originalStyles;
      };

      // Create a title page for the PDF
      const createTitlePage = () => {
        const titlePage = document.createElement('div');
        titlePage.id = 'title-page';
        titlePage.classList.add('title-page');
        
        // Style the title page
        const titlePageEl = titlePage as HTMLElement;
        titlePageEl.style.display = 'flex';
        titlePageEl.style.flexDirection = 'column';
        titlePageEl.style.justifyContent = 'center';
        titlePageEl.style.alignItems = 'center';
        titlePageEl.style.height = '100vh';
        titlePageEl.style.margin = '0';
        titlePageEl.style.padding = '2cm';
        titlePageEl.style.textAlign = 'center';
        titlePageEl.style.pageBreakAfter = 'always';
        titlePageEl.style.breakAfter = 'page';
        
        // Create and add title
        const title = document.createElement('h1');
        title.textContent = 'LinkedIn Networking: A Beginner\'s Guide';
        (title as HTMLElement).style.fontSize = '36pt';
        (title as HTMLElement).style.color = '#0077b5'; // LinkedIn blue
        (title as HTMLElement).style.marginBottom = '2cm';
        titlePage.appendChild(title);
        
        // Add subtitle
        const subtitle = document.createElement('h2');
        subtitle.textContent = 'Build Your Personal Brand & Grow Your Professional Network';
        (subtitle as HTMLElement).style.fontSize = '20pt';
        (subtitle as HTMLElement).style.marginBottom = '1cm';
        titlePage.appendChild(subtitle);
        
        // Add date
        const date = document.createElement('p');
        date.textContent = new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        (date as HTMLElement).style.fontSize = '14pt';
        (date as HTMLElement).style.marginTop = '2cm';
        titlePage.appendChild(date);
        
        // Insert at the beginning
        contentClone.insertBefore(titlePage, contentClone.firstChild);
      };
      
      // Add PDF-specific styling
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        ${extractOriginalStyles()}
        
        @page {
          margin: 1.5cm;
          size: A4;
        }
        
        body {
          font-family: 'Arial', 'Helvetica', sans-serif;
          line-height: 1.5;
          color: #333333;
          background-color: #ffffff;
        }
        
        h1, h2, h3, h4, h5, h6 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          page-break-after: avoid;
          break-after: avoid;
          color: #0077b5;
        }
        
        p {
          margin-bottom: 0.5em;
          orphans: 3;
          widows: 3;
        }
        
        section {
          page-break-before: always;
          break-before: page;
          padding-top: 1cm;
        }
        
        section:first-of-type {
          page-break-before: avoid;
          break-before: avoid;
        }
        
        ul, ol {
          margin-bottom: 1em;
          padding-left: 2em;
        }
        
        li {
          margin-bottom: 0.5em;
        }
        
        .title-page {
          page-break-after: always;
          break-after: page;
        }
        
        img {
          max-width: 100%;
          height: auto;
        }
        
        blockquote {
          margin: 1em 0;
          padding: 0.5em 1em;
          border-left: 4px solid #0077b5;
          background-color: #f5f5f5;
        }

        .bg-blue-50, .bg-yellow-50 {
          background-color: #f5f9ff;
          padding: 1em;
          margin: 1em 0;
          border-radius: 4px;
          border: 1px solid #e1e7ed;
        }
        
        .bg-yellow-50 {
          background-color: #fffbf0;
          border-color: #f0e3c2;
        }
        
        @media print {
          .no-print {
            display: none !important;
          }
        }
      `;
      
      createTitlePage();
      
      // Create a table of contents
      const createTableOfContents = () => {
        const tocSection = document.createElement('div');
        tocSection.classList.add('table-of-contents');
        
        const tocTitle = document.createElement('h2');
        tocTitle.textContent = 'Table of Contents';
        tocSection.appendChild(tocTitle);
        
        const tocList = document.createElement('ul');
        tocList.style.listStyleType = 'none';
        tocList.style.paddingLeft = '0';
        
        // Find all section headings
        const headings = contentClone.querySelectorAll('h2');
        let pageNumber = 3; // Start after title page and TOC
        
        headings.forEach((heading, index) => {
          if (heading.textContent && !heading.textContent.includes('Table of Contents')) {
            const listItem = document.createElement('li');
            listItem.style.marginBottom = '0.5em';
            
            const headingText = heading.textContent.trim();
            listItem.innerHTML = `${headingText} <span style="float: right;">${pageNumber}</span>`;
            
            tocList.appendChild(listItem);
            pageNumber++;
          }
        });
        
        tocSection.appendChild(tocList);
        
        // Add page break after TOC
        tocSection.style.pageBreakAfter = 'always';
        tocSection.style.breakAfter = 'page';
        
        // Insert after title page
        contentClone.insertBefore(tocSection, contentClone.firstElementChild?.nextElementSibling || null);
      };
      
      createTableOfContents();
      
      // Add page numbers
      const addPageNumbers = () => {
        const pageNumberScript = document.createElement('script');
        pageNumberScript.textContent = `
          // This script will run when the PDF is generated
          function addPageNumbers() {
            const totalPages = document.querySelectorAll('.page').length;
            document.querySelectorAll('.page').forEach((page, index) => {
              const pageNumber = document.createElement('div');
              pageNumber.classList.add('page-number');
              pageNumber.textContent = (index + 1) + ' / ' + totalPages;
              pageNumber.style.position = 'absolute';
              pageNumber.style.bottom = '1cm';
              pageNumber.style.right = '1cm';
              pageNumber.style.fontSize = '10pt';
              pageNumber.style.color = '#888888';
              page.appendChild(pageNumber);
            });
          }
          
          // Call the function when the PDF is being generated
          if (typeof window !== 'undefined') {
            window.addEventListener('DOMContentLoaded', addPageNumbers);
          }
        `;
        contentClone.appendChild(pageNumberScript);
      };
      
      addPageNumbers();
      
      // Add metadata
      const addMetadata = () => {
        const metaTitle = document.createElement('title');
        metaTitle.textContent = 'LinkedIn Networking: A Beginner\'s Guide';
        
        const metaAuthor = document.createElement('meta');
        metaAuthor.setAttribute('name', 'author');
        metaAuthor.setAttribute('content', 'Explanatum');
        
        const metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        metaDescription.setAttribute('content', 'A comprehensive guide to LinkedIn networking and personal brand building for beginners.');
        
        // Add metadata to head
        const head = contentClone.querySelector('head') || document.createElement('head');
        head.appendChild(metaTitle);
        head.appendChild(metaAuthor);
        head.appendChild(metaDescription);
        
        if (!contentClone.querySelector('head')) {
          contentClone.insertBefore(head, contentClone.firstChild);
        }
      };
      
      addMetadata();
      
      contentClone.appendChild(styleEl);
      
      return contentClone.outerHTML;
    } catch (error) {
      console.error('Error preparing content for PDF:', error);
      return contentElement.outerHTML;
    }
  };

  const handleExportPDF = async () => {
    if (!contentRef.current) {
      setError('Content reference not found.');
      return;
    }
    
    setIsExporting(true);
    setError(null);
    
    try {
      // Get HTML content and apply PDF-specific styling
      const contentHtml = prepareContentForPDF(contentRef.current);
      
      // Dynamically import html2pdf to avoid bundling issues
      const html2pdf = (await import('html2pdf.js')).default;
      
      // PDF generation options
      const opt = {
        margin: 1,
        filename: 'linkedin-networking-guide.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          letterRendering: true,
          useCORS: true
        },
        jsPDF: { 
          unit: 'cm', 
          format: 'a4', 
          orientation: 'portrait',
          putTotalPages: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      // Create a temporary div to hold our styled content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentHtml;
      document.body.appendChild(tempDiv);
      
      // Generate PDF from the temporary div
      await html2pdf().from(tempDiv).set(opt).save();
      
      // Remove the temporary div after PDF generation
      document.body.removeChild(tempDiv);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button 
      onClick={handleExportPDF}
      disabled={isExporting}
      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
    >
      {isExporting ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Generating PDF...
        </>
      ) : (
        <>
          <svg className="w-4 h-4 mr-2" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z"/>
          </svg>
          Download as PDF
        </>
      )}
    </button>
  );
};

export default LinkedInGuidePDFExport;
